import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputAdornment,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Switch,
  FormControlLabel,
  Divider,
  TablePagination,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Restaurant as RestaurantIcon,
  Coffee as CoffeeIcon,
  Wc as WcIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  FilterList as FilterListIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Tv as TvIcon,
  Computer as ComputerIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { TimezoneService } from '../services/timezoneService';
import moment from 'moment-timezone';
import { 
  saveLiveStatusConfig, 
  loadLiveStatusConfig, 
  resetLiveStatusConfig,
  exportLiveStatusConfig,
  importLiveStatusConfig,
  LiveStatusConfig 
} from '../utils/liveStatusConfig';

interface EmployeeStatus {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  departmentId: string;
  departmentName: string;
  currentStatus: 'clocked_in' | 'clocked_out' | 'on_break' | 'on_lunch' | 'on_bathroom';
  currentCampaignId: string;
  currentCampaignName: string;
  statusStartTime: string;
  statusDuration: number; // in minutes
  lastActivity: string;
  workLocation: 'on_site' | 'remote';
  isIdle?: boolean;
  idleStartTime?: string;
  idleDurationMinutes?: number;
  idleDurationHours?: number;
}

const LiveEmployeeStatusPage: React.FC = () => {
  const { user } = useAuth();
  const { sidebarCollapsed } = useSidebar();
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Load configuration on component mount
  const [config, setConfig] = useState<LiveStatusConfig>(() => loadLiveStatusConfig());
  
  // Filter states from config
  const [searchTerm, setSearchTerm] = useState(config.searchTerm);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(config.selectedStatuses);
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>(config.selectedCampaigns);
  const [refreshInterval, setRefreshInterval] = useState(config.refreshInterval);
  const [showFilters, setShowFilters] = useState(config.showFilters);
  const [tvMode, setTvMode] = useState(config.tvMode);
  
  // UI states
  const [activeTab, setActiveTab] = useState(0);
  
  // Debug: Log active tab changes
  useEffect(() => {
    console.log('🔄 Active tab changed to:', activeTab);
  }, [activeTab]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6); // Show 6 employees at a time like airport monitors
  const [autoScroll, setAutoScroll] = useState(true); // Auto-scroll like airport monitors
  
  // Time limits for highlighting (minutes). Bathroom uses authoritative
  // bathroom_alert_threshold_seconds (default 600 → 10 min). Convention: duration >= limit ⇒ over.
  const BREAK_TIME_LIMIT = 15; // 15 minutes
  const DEFAULT_BATHROOM_TIME_LIMIT_MINUTES = 10; // 600 seconds default
  const LUNCH_TIME_LIMIT = 60; // 1 hour
  const [bathroomTimeLimitMinutes, setBathroomTimeLimitMinutes] = useState(DEFAULT_BATHROOM_TIME_LIMIT_MINUTES);

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    // Auto-scroll horizontally to show the current page content
    setTimeout(() => {
      const displayView = document.querySelector('[data-display-view]');
      if (displayView) {
        // Scroll to top and left to show the new page content
        displayView.scrollTo({ 
          top: 0, 
          left: 0, 
          behavior: 'smooth' 
        });
      }
    }, 100);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Auto-scroll effect like airport monitors - moved after filteredEmployees is defined

  // Configuration management functions
  const saveConfig = () => {
    const newConfig: LiveStatusConfig = {
      searchTerm,
      selectedStatuses,
      selectedCampaigns,
      refreshInterval,
      showFilters,
      tvMode,
    };
    setConfig(newConfig);
    saveLiveStatusConfig(newConfig);
    setSnackbarMessage('Configuration saved successfully!');
    setSnackbarOpen(true);
  };

  const loadConfig = () => {
    const loadedConfig = loadLiveStatusConfig();
    setConfig(loadedConfig);
    setSearchTerm(loadedConfig.searchTerm);
    setSelectedStatuses(loadedConfig.selectedStatuses);
    setSelectedCampaigns(loadedConfig.selectedCampaigns);
    setRefreshInterval(loadedConfig.refreshInterval);
    setShowFilters(loadedConfig.showFilters);
    setTvMode(loadedConfig.tvMode);
    setSnackbarMessage('Configuration loaded successfully!');
    setSnackbarOpen(true);
  };

  const resetConfig = () => {
    const defaultConfig = resetLiveStatusConfig();
    setConfig(defaultConfig);
    setSearchTerm(defaultConfig.searchTerm);
    setSelectedStatuses(defaultConfig.selectedStatuses);
    setSelectedCampaigns(defaultConfig.selectedCampaigns);
    setRefreshInterval(defaultConfig.refreshInterval);
    setShowFilters(defaultConfig.showFilters);
    setTvMode(defaultConfig.tvMode);
    setSnackbarMessage('Configuration reset to defaults!');
    setSnackbarOpen(true);
  };

  const exportConfig = () => {
    const currentConfig: LiveStatusConfig = {
      searchTerm,
      selectedStatuses,
      selectedCampaigns,
      refreshInterval,
      showFilters,
      tvMode,
    };
    exportLiveStatusConfig(currentConfig);
    setSnackbarMessage('Configuration exported successfully!');
    setSnackbarOpen(true);
  };

  const importConfig = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      importLiveStatusConfig(file)
        .then((importedConfig) => {
          setConfig(importedConfig);
          setSearchTerm(importedConfig.searchTerm);
          setSelectedStatuses(importedConfig.selectedStatuses);
          setSelectedCampaigns(importedConfig.selectedCampaigns);
          setRefreshInterval(importedConfig.refreshInterval);
          setShowFilters(importedConfig.showFilters);
          setTvMode(importedConfig.tvMode);
          setSnackbarMessage('Configuration imported successfully!');
          setSnackbarOpen(true);
        })
        .catch((error) => {
          setSnackbarMessage(`Failed to import configuration: ${error.message}`);
          setSnackbarOpen(true);
        });
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Get live employee status data
  const { data: statusData, isLoading, error, refetch } = useQuery({
    queryKey: ['liveEmployeeStatus'],
    queryFn: async () => {
      const response = await apiClient.timeEntries.getLiveStatus();
      return response.data;
    },
    refetchInterval: refreshInterval,
    enabled: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Authoritative Bathroom over-limit threshold (seconds → minutes for statusDuration).
  useQuery({
    queryKey: ['monitoringBathroomAlertThreshold'],
    queryFn: async () => {
      const response = await apiClient.monitoring.getConfiguration();
      const seconds = Number(response?.data?.data?.work_session?.bathroom_alert_threshold_seconds ?? 600);
      const minutes = Number.isFinite(seconds) && seconds > 0 ? seconds / 60 : DEFAULT_BATHROOM_TIME_LIMIT_MINUTES;
      setBathroomTimeLimitMinutes(minutes);
      return minutes;
    },
    enabled: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
    staleTime: 60_000,
  });

  // Get currently idle employees
  const { data: idleEmployeesData } = useQuery({
    queryKey: ['currentlyIdleEmployees'],
    queryFn: () => apiClient.activityTracking.getCurrentlyIdle(),
    enabled: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
    refetchInterval: refreshInterval,
  });

  // Get all campaigns for the filter dropdown
  const { data: campaignsData, isLoading: campaignsLoading, error: campaignsError } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => apiClient.campaigns.getAll(),
    enabled: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Debug campaigns loading state
  useEffect(() => {
    console.log('📊 Campaigns loading state:', { campaignsLoading, campaignsError, campaignsData });
  }, [campaignsLoading, campaignsError, campaignsData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [refetch, refreshInterval]);

  // Get employees data and merge with idle status
  const employees: EmployeeStatus[] = useMemo(() => {
    const baseEmployees: EmployeeStatus[] = statusData?.data || [];
    const idleEmployees = idleEmployeesData?.data?.data || [];
    
    // Create a map of userId to idle info
    const idleMap = new Map();
    idleEmployees.forEach((idle: any) => {
      idleMap.set(idle.userId, idle);
    });
    
    // Create a map of employee IDs for quick lookup
    const employeeMap = new Map();
    baseEmployees.forEach((emp: EmployeeStatus) => {
      employeeMap.set(emp.id, emp);
    });
    
    // Merge idle status into existing employee data.
    // Precedence: Break / Bathroom / Lunch disposition beats stale Idle signal.
    const dispositionStatuses = new Set(['on_break', 'on_bathroom', 'on_lunch']);
    const mergedEmployees = baseEmployees.map((emp: EmployeeStatus) => {
      const idleInfo = idleMap.get(emp.id);
      if (idleInfo && !dispositionStatuses.has(emp.currentStatus)) {
        // Add idle status to employee (don't require clocked_in status)
        return {
          ...emp,
          isIdle: true,
          idleStartTime: idleInfo.idleStartTime,
          idleDurationMinutes: idleInfo.idleDurationMinutes,
          idleDurationHours: idleInfo.idleDurationHours,
        };
      }
      return emp;
    });
    
    // Add idle employees who are not in the base status data
    // This handles cases where an employee is idle but doesn't have a time entry
    idleEmployees.forEach((idle: any) => {
      if (!employeeMap.has(idle.userId)) {
        // Create a minimal employee entry for idle employees not in statusData
        mergedEmployees.push({
          id: idle.userId,
          employeeId: idle.employeeId || '',
          firstName: idle.userName?.split(' ')[0] || 'Unknown',
          lastName: idle.userName?.split(' ').slice(1).join(' ') || 'User',
          email: idle.email || '',
          role: '',
          departmentId: '',
          departmentName: '',
          currentStatus: 'clocked_in' as const, // Assume clocked in if idle
          currentCampaignId: '',
          currentCampaignName: '',
          statusStartTime: idle.idleStartTime || new Date().toISOString(),
          statusDuration: idle.idleDurationMinutes || 0,
          lastActivity: idle.idleStartTime || new Date().toISOString(),
          workLocation: 'on_site' as const,
          isIdle: true,
          idleStartTime: idle.idleStartTime,
          idleDurationMinutes: idle.idleDurationMinutes,
          idleDurationHours: idle.idleDurationHours,
        });
      }
    });
    
    return mergedEmployees;
  }, [statusData?.data, idleEmployeesData?.data]);

  // Get unique campaigns from all campaigns data
  const uniqueCampaigns = useMemo(() => {
    console.log('🔍 Campaigns data:', campaignsData);
    
    // Check if campaignsData has the expected structure and data.data is an array
    if (campaignsData?.data?.data && Array.isArray(campaignsData.data.data)) {
      const campaignNames = campaignsData.data.data
        .filter(campaign => campaign.isActive)
        .map(campaign => campaign.name)
        .sort();
      console.log('📋 Active campaign names:', campaignNames);
      return campaignNames;
    }
    
    // Fallback: get campaigns from current employee data
    const campaigns = new Set<string>();
    employees.forEach(emp => {
      if (emp.currentCampaignName) {
        campaigns.add(emp.currentCampaignName);
      }
    });
    const fallbackCampaigns = Array.from(campaigns).sort();
    console.log('🔄 Fallback campaigns from employees:', fallbackCampaigns);
    return fallbackCampaigns;
  }, [campaignsData, employees]);

  // Filter employees based on search and filters
  // Exclude administrators and super_users (they typically don't have schedules)
  const filteredEmployees = useMemo(() => {
    return employees.filter(employee => {
      // Exclude administrators and super_users
      if (employee.role === 'administrator' || employee.role === 'super_user') {
        return false;
      }

      // Search filter
      const matchesSearch = searchTerm === '' || 
        employee.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.departmentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (employee.currentCampaignName && employee.currentCampaignName.toLowerCase().includes(searchTerm.toLowerCase()));

      // Status filter
      const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(employee.currentStatus);

      // Campaign filter
      const matchesCampaign = selectedCampaigns.length === 0 || 
        (employee.currentCampaignName && selectedCampaigns.includes(employee.currentCampaignName));

      return matchesSearch && matchesStatus && matchesCampaign;
    });
  }, [employees, searchTerm, selectedStatuses, selectedCampaigns]);

  // Calculate actual rows displayed and adjust pagination accordingly
  useEffect(() => {
    if (activeTab !== 1) return; // Only in display view
    
    const calculateOptimalRows = () => {
      // Count rows that can fit in the available space
      const tableRows = document.querySelectorAll('[data-display-view] tbody tr');
      const tableContainer = document.querySelector('[data-display-view] .MuiTableContainer-root');
      
      if (!tableContainer || tableRows.length === 0) return;
      
      const containerRect = tableContainer.getBoundingClientRect();
      const availableHeight = containerRect.height - 60; // Account for table header
      
      // Get the height of one row
      const firstRow = tableRows[0];
      const rowHeight = firstRow.getBoundingClientRect().height;
      
      // Calculate how many rows can fit
      const maxRowsThatCanFit = Math.floor(availableHeight / rowHeight);
      
      // Use the minimum of available rows and rows that can fit
      const optimalRows = Math.min(tableRows.length, maxRowsThatCanFit);
      
      console.log('🔄 Available height:', availableHeight, 'Row height:', rowHeight, 'Max rows that can fit:', maxRowsThatCanFit, 'Optimal rows:', optimalRows);
      
      // If the optimal rows don't match the rowsPerPage, adjust it
      if (optimalRows > 0 && optimalRows !== rowsPerPage) {
        console.log('🔄 Adjusting rowsPerPage from', rowsPerPage, 'to', optimalRows);
        setRowsPerPage(optimalRows);
        setPage(0); // Reset to first page
      }
    };
    
    // Use a timeout to ensure DOM is ready
    const timeoutId = setTimeout(calculateOptimalRows, 200);
    
    // Add resize listener for responsive behavior
    const handleResize = () => {
      setTimeout(calculateOptimalRows, 100);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [activeTab, filteredEmployees.length, rowsPerPage]);

  // Auto-scroll effect like airport monitors
  useEffect(() => {
    if (!autoScroll || activeTab !== 1) return; // Only auto-scroll in display view
    
    const totalEmployeesCount = filteredEmployees.length;
    const totalPages = Math.ceil(totalEmployeesCount / rowsPerPage);
    if (totalPages <= 1) return; // No need to scroll if only one page
    
    const scrollTimer = setInterval(() => {
      setPage(prevPage => {
        const nextPage = (prevPage + 1) % totalPages;
        return nextPage;
      });
    }, 12000); // Change page every 12 seconds - slower timing

    return () => clearInterval(scrollTimer);
  }, [autoScroll, activeTab, filteredEmployees.length, rowsPerPage]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'clocked_in':
        return 'success';
      case 'on_break':
        return 'warning';
      case 'on_bathroom':
        return 'secondary';
      case 'on_lunch':
        return 'info';
      case 'clocked_out':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'clocked_in':
        return <CheckCircleIcon />;
      case 'on_break':
        return <CoffeeIcon />;
      case 'on_bathroom':
        return <WcIcon />;
      case 'on_lunch':
        return <RestaurantIcon />;
      case 'clocked_out':
        return <CancelIcon />;
      default:
        return <PauseIcon />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'clocked_in':
        return 'Working';
      case 'on_break':
        return 'On Break';
      case 'on_bathroom':
        return 'On Bathroom';
      case 'on_lunch':
        return 'On Lunch';
      case 'clocked_out':
        return 'Offline';
      default:
        return 'Unknown';
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getWorkLocationColor = (location: string) => {
    return location === 'on_site' ? 'primary' : 'secondary';
  };

  const getWorkLocationIcon = (location: string) => {
    return location === 'on_site' ? <WorkIcon /> : <PersonIcon />;
  };

  // Check if employee exceeds time limits or is idle
  // Over-limit convention: statusDuration >= limit (exact threshold counts as over).
  const isExceedingTimeLimit = (employee: EmployeeStatus) => {
    if (employee.isIdle) {
      return true; // Idle employees should be highlighted
    }
    if (employee.currentStatus === 'on_break' && employee.statusDuration >= BREAK_TIME_LIMIT) {
      return true;
    }
    if (employee.currentStatus === 'on_bathroom' && employee.statusDuration >= bathroomTimeLimitMinutes) {
      return true;
    }
    if (employee.currentStatus === 'on_lunch' && employee.statusDuration >= LUNCH_TIME_LIMIT) {
      return true;
    }
    return false;
  };

  // Get highlighting color for exceeded time limits or idle status
  const getExceededTimeColor = (employee: EmployeeStatus) => {
    if (employee.isIdle) {
      return '#fff3e0'; // Light orange background (same as lunch exceeding)
    }
    if (
      (employee.currentStatus === 'on_break' && employee.statusDuration >= BREAK_TIME_LIMIT) ||
      (employee.currentStatus === 'on_bathroom' && employee.statusDuration >= bathroomTimeLimitMinutes)
    ) {
      return '#ffebee'; // Light red background
    }
    if (employee.currentStatus === 'on_lunch' && employee.statusDuration >= LUNCH_TIME_LIMIT) {
      return '#fff3e0'; // Light orange background
    }
    return 'transparent';
  };


  // Group filtered employees by status
  const statusGroups = filteredEmployees.reduce((acc, employee) => {
    if (!acc[employee.currentStatus]) {
      acc[employee.currentStatus] = [];
    }
    acc[employee.currentStatus].push(employee);
    return acc;
  }, {} as Record<string, EmployeeStatus[]>);

  // Count idle employees (those with isIdle flag set to true)
  const idleCount = filteredEmployees.filter(emp => emp.isIdle === true).length;

  const statusCounts = {
    clocked_in: statusGroups.clocked_in?.length || 0,
    on_break: statusGroups.on_break?.length || 0,
    on_bathroom: statusGroups.on_bathroom?.length || 0,
    on_lunch: statusGroups.on_lunch?.length || 0,
    clocked_out: statusGroups.clocked_out?.length || 0,
    idle: idleCount,
  };

  const totalEmployees = filteredEmployees.length;

  // Paginated employees for display view
  const paginatedEmployees = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredEmployees.slice(startIndex, endIndex);
  }, [filteredEmployees, page, rowsPerPage]);

  if (!['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '')) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to access this page.
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load live status data. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Tabs for different views - Moved to very top */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="live status tabs">
          <Tab 
            icon={<ComputerIcon />} 
            label="Dashboard View" 
            iconPosition="start"
          />
          <Tab 
            icon={<TvIcon />} 
            label="Display View" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Header - Only show in Dashboard View */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Live Employee Status
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {/* Live Clock - More Visible */}
            <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            backgroundColor: '#f0f7ff',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '2px solid #e3f2fd',
            minWidth: '160px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="body1" sx={{ 
              color: 'primary.main',
              fontSize: '0.9rem',
              fontWeight: 600,
              mb: 0.5
            }}>
              {currentTime.toLocaleDateString('en-US', { 
                weekday: 'short',
                month: 'short', 
                day: 'numeric',
                year: 'numeric'
              })}
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'primary.main',
              fontSize: '1.25rem',
              fontWeight: 700,
              fontFamily: 'monospace',
              letterSpacing: '1px',
              textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
            }}>
              {currentTime.toLocaleTimeString('en-US', { 
                hour12: true, 
                hour: '2-digit', 
                minute: '2-digit',
                second: '2-digit'
              })}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Auto-refresh: {refreshInterval / 1000}s
            </Typography>
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={saveConfig} color="primary" title="Save Configuration">
              <SaveIcon />
            </IconButton>
            <IconButton onClick={() => setSettingsOpen(true)} color="primary" title="Settings">
              <SettingsIcon />
            </IconButton>
          </Box>
          </Box>
        </Box>
      )}

      {/* Dashboard View Tab Panel */}
      {activeTab === 0 && (
        <Box>
          {/* Status Summary Cards */}
          <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <Card sx={{ flex: 1, minWidth: { xs: '100%', sm: '200px' } }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Working
                    </Typography>
                    <Typography variant="h4" component="div" color="success.main">
                      {statusCounts.clocked_in}
                    </Typography>
                  </Box>
                  <Box color="success.main">
                    <CheckCircleIcon sx={{ fontSize: 40 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: { xs: '100%', sm: '200px' } }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      On Break
                    </Typography>
                    <Typography variant="h4" component="div" color="warning.main">
                      {statusCounts.on_break}
                    </Typography>
                  </Box>
                  <Box color="warning.main">
                    <CoffeeIcon sx={{ fontSize: 40 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: { xs: '100%', sm: '200px' } }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      On Bathroom
                    </Typography>
                    <Typography variant="h4" component="div" color="warning.main">
                      {statusCounts.on_bathroom}
                    </Typography>
                  </Box>
                  <Box color="warning.main">
                    <WcIcon sx={{ fontSize: 40 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: { xs: '100%', sm: '200px' } }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      On Lunch
                    </Typography>
                    <Typography variant="h4" component="div" color="info.main">
                      {statusCounts.on_lunch}
                    </Typography>
                  </Box>
                  <Box color="info.main">
                    <RestaurantIcon sx={{ fontSize: 40 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: { xs: '100%', sm: '200px' } }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Offline
                    </Typography>
                    <Typography variant="h4" component="div" color="error.main">
                      {statusCounts.clocked_out}
                    </Typography>
                  </Box>
                  <Box color="error.main">
                    <CancelIcon sx={{ fontSize: 40 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: { xs: '100%', sm: '200px' } }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Idle
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ color: '#9c27b0' }}>
                      {statusCounts.idle}
                    </Typography>
                  </Box>
                  <Box sx={{ color: '#9c27b0' }}>
                    <PauseIcon sx={{ fontSize: 40 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

      {/* Filters Section - Only show if enabled */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FilterListIcon color="primary" />
            <Typography variant="h6">Filters</Typography>
          </Box>
          
          <Grid container spacing={3}>
            {/* Search Box */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search Employees"
                placeholder="Search by name, ID, department, or campaign..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  multiple
                  value={selectedStatuses}
                  onChange={(e) => setSelectedStatuses(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  input={<OutlinedInput label="Filter by Status" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={getStatusLabel(value)} 
                          size="small"
                          color={getStatusColor(value) as any}
                          icon={getStatusIcon(value)}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {['clocked_in', 'on_break', 'on_bathroom', 'on_lunch', 'clocked_out'].map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={selectedStatuses.indexOf(status) > -1} />
                      <ListItemText 
                        primary={getStatusLabel(status)}
                        secondary={`${statusGroups[status]?.length || 0} employees`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Campaign Filter */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Campaign</InputLabel>
                <Select
                  multiple
                  value={selectedCampaigns}
                  onChange={(e) => setSelectedCampaigns(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  input={<OutlinedInput label="Filter by Campaign" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  )}
                >
                  {uniqueCampaigns.map((campaign) => (
                    <MenuItem key={campaign} value={campaign}>
                      <Checkbox checked={selectedCampaigns.indexOf(campaign) > -1} />
                      <ListItemText 
                        primary={campaign}
                        secondary={`${employees.filter(emp => emp.currentCampaignName === campaign).length} active employees`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Filter Summary */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {totalEmployees} of {employees.length} employees
            </Typography>
            {(searchTerm || selectedStatuses.length > 0 || selectedCampaigns.length > 0) && (
              <Chip
                label="Clear Filters"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedStatuses([]);
                  setSelectedCampaigns([]);
                }}
                color="default"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </CardContent>
      </Card>
      )}

      {/* Employee Status Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Employee Status Details ({totalEmployees} total employees)
            </Typography>
            
            {/* Time Limit Legend */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: '#ffebee', border: '1px solid #f44336', borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Break &gt; {BREAK_TIME_LIMIT}min
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: '#ffebee', border: '1px solid #f44336', borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Bathroom &gt;= {bathroomTimeLimitMinutes}min
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: '#fff3e0', border: '1px solid #ff9800', borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Lunch &gt; {LUNCH_TIME_LIMIT}min
                </Typography>
              </Box>
            </Box>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Last Activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.map((employee) => {
                  const isExceeding = isExceedingTimeLimit(employee);
                  const highlightColor = getExceededTimeColor(employee);
                  
                  return (
                    <TableRow 
                      key={employee.id}
                      sx={{
                        backgroundColor: highlightColor,
                        '&:hover': {
                          backgroundColor: isExceeding ? 
                            (employee.isIdle
                              ? '#ffe0b2'
                              : (employee.currentStatus === 'on_break' || employee.currentStatus === 'on_bathroom')
                                ? '#ffcdd2'
                                : '#ffe0b2') : 
                            'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {employee.firstName[0]}{employee.lastName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {employee.firstName} {employee.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {employee.employeeId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {employee.departmentName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            icon={getStatusIcon(employee.currentStatus)}
                            label={employee.isIdle ? 'Idle' : getStatusLabel(employee.currentStatus)}
                            color={employee.isIdle ? 'warning' : (getStatusColor(employee.currentStatus) as any)}
                            size="small"
                          />
                          {isExceeding && (
                            <Tooltip title={
                              employee.isIdle
                                ? `Idle for ${employee.idleDurationMinutes || 0} minutes`
                                : employee.currentStatus === 'on_break' 
                                  ? `Break exceeded ${BREAK_TIME_LIMIT} minutes limit`
                                  : employee.currentStatus === 'on_bathroom'
                                    ? `Bathroom exceeded ${bathroomTimeLimitMinutes} minutes limit`
                                    : `Lunch exceeded ${LUNCH_TIME_LIMIT} minutes limit`
                            }>
                              <WarningIcon color="error" fontSize="small" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {employee.currentCampaignName || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={
                          employee.isIdle
                            ? `Idle since ${moment(employee.idleStartTime).format('MMM D, HH:mm')} (${employee.idleDurationMinutes || 0} minutes)`
                            : `Since ${TimezoneService.formatDateTimeConsistent(employee.statusStartTime, user?.timezone || TimezoneService.getUserTimezone())}`
                        }>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon fontSize="small" />
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              color={isExceeding ? 'error.main' : 'inherit'}
                            >
                              {employee.isIdle 
                                ? `${employee.idleDurationMinutes || 0}m idle`
                                : formatDuration(employee.statusDuration)}
                            </Typography>
                            {isExceeding && (
                              <Typography variant="caption" color="error.main" fontWeight="bold">
                                {employee.isIdle ? '(Idle)' : '(Exceeded)'}
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getWorkLocationIcon(employee.workLocation)}
                          label={employee.workLocation === 'on_site' ? 'On-Site' : 'Remote'}
                          color={getWorkLocationColor(employee.workLocation) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {TimezoneService.formatDateTimeConsistent(employee.lastActivity, user?.timezone || TimezoneService.getUserTimezone())}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
        </Box>
      )}

      {/* Display View Tab Panel */}
      {activeTab === 1 && (
        <Box 
          data-display-view
          sx={{ 
            backgroundColor: '#000', 
            color: '#fff', 
            minHeight: '100vh', // Full viewport height
            padding: 2,
            position: 'fixed',
            top: 0, // Start from top
            left: sidebarCollapsed ? '64px' : '240px', // Responsive to sidebar state
            right: 0,
            bottom: 0,
            width: sidebarCollapsed ? 'calc(100vw - 64px)' : 'calc(100vw - 240px)', // Responsive width
            height: '100vh', // Full viewport height
            overflow: 'auto',
            zIndex: 1000,
            paddingTop: '80px', // Account for header
            paddingBottom: '20px' // Reduced bottom padding to use more space
          }}>
          {/* Control Buttons */}
          <Box sx={{ 
            position: 'fixed', 
            top: 200, 
            right: 20, 
            zIndex: 9999,
            display: 'flex',
            gap: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: 1,
            borderRadius: 1
          }}>
            <Button
              variant="contained"
              onClick={() => setAutoScroll(!autoScroll)}
              sx={{
                backgroundColor: autoScroll ? '#4caf50' : '#666',
                color: '#fff',
                '&:hover': {
                  backgroundColor: autoScroll ? '#45a049' : '#555',
                },
                minWidth: 'auto',
                px: 1.5,
                py: 0.75,
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }}
            >
              {autoScroll ? '⏸️ Pause' : '▶️ Auto'}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                console.log('🔄 Dashboard View button clicked!');
                setActiveTab(0);
              }}
              sx={{
                backgroundColor: '#1976d2',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
                minWidth: 'auto',
                px: 2,
                py: 1,
                fontSize: '0.85rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                border: '2px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              ← Dashboard View
            </Button>
          </Box>

          {/* Display Mode - Maximized space, no header */}

          {/* Display Mode Status Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <Card sx={{ 
              flex: 1,
              minWidth: { xs: '100%', sm: '180px' },
              backgroundColor: '#2e7d32', 
              color: '#fff',
              border: '3px solid #4caf50',
              boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)'
            }}>
              <CardContent sx={{ padding: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>
                      WORKING
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {statusCounts.clocked_in}
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50' }} />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ 
              flex: 1,
              minWidth: { xs: '100%', sm: '180px' },
              backgroundColor: '#f57c00', 
              color: '#fff',
              border: '3px solid #ff9800',
              boxShadow: '0 8px 32px rgba(255, 152, 0, 0.3)'
            }}>
              <CardContent sx={{ padding: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>
                      ON BREAK
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {statusCounts.on_break}
                    </Typography>
                  </Box>
                  <CoffeeIcon sx={{ fontSize: 40, color: '#ff9800' }} />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ 
              flex: 1,
              minWidth: { xs: '100%', sm: '180px' },
              backgroundColor: '#f9a825', 
              color: '#fff',
              border: '3px solid #fbc02d',
              boxShadow: '0 8px 32px rgba(251, 192, 45, 0.3)'
            }}>
              <CardContent sx={{ padding: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>
                      ON BATHROOM
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {statusCounts.on_bathroom}
                    </Typography>
                  </Box>
                  <WcIcon sx={{ fontSize: 40, color: '#fbc02d' }} />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ 
              flex: 1,
              minWidth: { xs: '100%', sm: '180px' },
              backgroundColor: '#1976d2', 
              color: '#fff',
              border: '3px solid #2196f3',
              boxShadow: '0 8px 32px rgba(33, 150, 243, 0.3)'
            }}>
              <CardContent sx={{ padding: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>
                      ON LUNCH
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {statusCounts.on_lunch}
                    </Typography>
                  </Box>
                  <RestaurantIcon sx={{ fontSize: 40, color: '#2196f3' }} />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ 
              flex: 1,
              minWidth: { xs: '100%', sm: '180px' },
              backgroundColor: '#d32f2f', 
              color: '#fff',
              border: '3px solid #f44336',
              boxShadow: '0 8px 32px rgba(244, 67, 54, 0.3)'
            }}>
              <CardContent sx={{ padding: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>
                      OFFLINE
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {statusCounts.clocked_out}
                    </Typography>
                  </Box>
                  <CancelIcon sx={{ fontSize: 40, color: '#f44336' }} />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ 
              flex: 1,
              minWidth: { xs: '100%', sm: '180px' },
              backgroundColor: '#7b1fa2', 
              color: '#fff',
              border: '3px solid #9c27b0',
              boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
            }}>
              <CardContent sx={{ padding: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>
                      IDLE
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {statusCounts.idle}
                    </Typography>
                  </Box>
                  <PauseIcon sx={{ fontSize: 40, color: '#ba68c8' }} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Display Mode Employee Table */}
          <Card sx={{ 
            backgroundColor: '#1a1a1a', 
            border: '2px solid #444',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
          }}>
            <CardContent sx={{ padding: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ 
                  color: '#fff', 
                  fontWeight: 'bold'
                }}>
                  EMPLOYEE STATUS DETAILS ({totalEmployees} TOTAL)
                </Typography>
                {totalEmployees > rowsPerPage && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ 
                      color: '#999', 
                      fontSize: '0.75rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      {page + 1}/{Math.ceil(totalEmployees / rowsPerPage)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        backgroundColor: autoScroll ? '#4caf50' : '#666',
                        animation: autoScroll ? 'pulse 2s infinite' : 'none'
                      }} />
                      <Typography variant="caption" sx={{ 
                        color: autoScroll ? '#4caf50' : '#666',
                        fontSize: '0.7rem'
                      }}>
                        {autoScroll ? 'AUTO' : 'MANUAL'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              
              <TableContainer 
                component={Paper} 
                sx={{ 
                  backgroundColor: '#2a2a2a',
                  border: '2px solid #444',
                  height: 'auto', // Let it be determined by content and available space
                  minHeight: '400px', // Minimum height
                  maxHeight: 'calc(100vh - 120px)', // Use more space - reduced from 200px to 120px
                  overflow: 'hidden', // Hide scrollbar for cleaner look
                  transition: 'all 0.5s ease-in-out' // Smooth transitions
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#333' }}>
                      <TableCell sx={{ 
                        color: '#fff', 
                        fontWeight: 'bold', 
                        fontSize: '1rem',
                        borderColor: '#444',
                        padding: 1.5
                      }}>
                        EMPLOYEE
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#fff', 
                        fontWeight: 'bold', 
                        fontSize: '1rem',
                        borderColor: '#444',
                        padding: 1.5
                      }}>
                        DEPARTMENT
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#fff', 
                        fontWeight: 'bold', 
                        fontSize: '1rem',
                        borderColor: '#444',
                        padding: 1.5
                      }}>
                        STATUS
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#fff', 
                        fontWeight: 'bold', 
                        fontSize: '1rem',
                        borderColor: '#444',
                        padding: 1.5
                      }}>
                        CAMPAIGN
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#fff', 
                        fontWeight: 'bold', 
                        fontSize: '1rem',
                        borderColor: '#444',
                        padding: 1.5
                      }}>
                        DURATION
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#fff', 
                        fontWeight: 'bold', 
                        fontSize: '1rem',
                        borderColor: '#444',
                        padding: 1.5
                      }}>
                        LOCATION
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ 
                    transition: 'opacity 0.3s ease-in-out',
                    opacity: 1
                  }}>
                    {paginatedEmployees.map((employee) => {
                      const isExceeding = isExceedingTimeLimit(employee);
                      const highlightColor = isExceeding ? 
                        ((employee.currentStatus === 'on_break' || employee.currentStatus === 'on_bathroom') ? '#ffebee' : '#fff3e0') : 
                        '#2a2a2a';
                      
                      return (
                        <TableRow 
                          key={employee.id}
                          sx={{
                            backgroundColor: highlightColor,
                            '&:hover': {
                              backgroundColor: isExceeding ? 
                                ((employee.currentStatus === 'on_break' || employee.currentStatus === 'on_bathroom') ? '#ffcdd2' : '#ffe0b2') : 
                                '#333'
                            }
                          }}
                        >
                          <TableCell sx={{ 
                            color: '#fff', 
                            fontSize: '0.9rem',
                            borderColor: '#444',
                            padding: 1.5
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ 
                                width: 32, 
                                height: 32,
                                backgroundColor: '#4fc3f7',
                                fontSize: '0.9rem',
                                fontWeight: 'bold'
                              }}>
                                {employee.firstName[0]}{employee.lastName[0]}
                              </Avatar>
                              <Box>
                                <Typography variant="body1" sx={{ 
                                  color: '#fff', 
                                  fontWeight: 'bold',
                                  fontSize: '0.9rem'
                                }}>
                                  {employee.firstName} {employee.lastName}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: '#ccc',
                                  fontSize: '0.8rem'
                                }}>
                                  {employee.employeeId}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ 
                            color: '#fff', 
                            fontSize: '0.9rem',
                            borderColor: '#444',
                            padding: 1.5
                          }}>
                            {employee.departmentName}
                          </TableCell>
                          <TableCell sx={{ borderColor: '#444', padding: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                icon={getStatusIcon(employee.currentStatus)}
                                label={employee.isIdle ? 'Idle' : getStatusLabel(employee.currentStatus)}
                                color={employee.isIdle ? 'warning' : (getStatusColor(employee.currentStatus) as any)}
                                sx={{ 
                                  fontSize: '0.8rem',
                                  height: '28px',
                                  fontWeight: 'bold'
                                }}
                              />
                              {isExceeding && (
                                <WarningIcon sx={{ 
                                  color: '#f44336', 
                                  fontSize: '1.2rem' 
                                }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ 
                            color: '#fff', 
                            fontSize: '0.9rem',
                            borderColor: '#444',
                            padding: 1.5
                          }}>
                            {employee.currentCampaignName || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ borderColor: '#444', padding: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccessTimeIcon sx={{ 
                                color: isExceeding ? '#f44336' : '#4fc3f7',
                                fontSize: '1.2rem'
                              }} />
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  color: isExceeding ? '#f44336' : '#fff',
                                  fontWeight: 'bold',
                                  fontSize: '0.9rem'
                                }}
                              >
                                {employee.isIdle 
                                  ? `${employee.idleDurationMinutes || 0}m idle`
                                  : formatDuration(employee.statusDuration)}
                              </Typography>
                              {isExceeding && (
                                <Typography variant="body2" sx={{ 
                                  color: '#f44336', 
                                  fontWeight: 'bold',
                                  fontSize: '0.8rem'
                                }}>
                                  {employee.isIdle ? '(IDLE)' : '(EXCEEDED)'}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderColor: '#444', padding: 1 }}>
                            <Chip
                              icon={getWorkLocationIcon(employee.workLocation)}
                              label={employee.workLocation === 'on_site' ? 'ON-SITE' : 'REMOTE'}
                              color={getWorkLocationColor(employee.workLocation) as any}
                              sx={{ 
                                fontSize: '0.8rem',
                                height: '28px',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination Controls */}
              {totalEmployees > rowsPerPage && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 0.5, // Reduced margin to use more space
                  backgroundColor: 'rgba(26, 26, 26, 0.8)',
                  padding: 1,
                  borderRadius: 1,
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 100,
                  backdropFilter: 'blur(4px)'
                }}>
                  <TablePagination
                    component="div"
                    count={totalEmployees}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[4, 6, 8, 10]}
                    labelRowsPerPage=""
                    labelDisplayedRows={({ from, to, count }) => 
                      `${from}-${to}`
                    }
                    sx={{
                      color: '#ccc',
                      '& .MuiTablePagination-toolbar': {
                        color: '#ccc',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        minHeight: 'auto',
                        padding: 0
                      },
                      '& .MuiTablePagination-selectLabel': {
                        color: '#ccc',
                        fontSize: '0.7rem',
                        display: 'none'
                      },
                      '& .MuiTablePagination-displayedRows': {
                        color: '#ccc',
                        fontSize: '0.7rem'
                      },
                      '& .MuiTablePagination-select': {
                        color: '#ccc',
                        fontSize: '0.7rem'
                      },
                      '& .MuiTablePagination-selectIcon': {
                        color: '#ccc',
                        fontSize: '0.8rem'
                      },
                      '& .MuiIconButton-root': {
                        color: '#ccc',
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SettingsIcon />
            Live Status Configuration
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Refresh Interval (seconds)</InputLabel>
                <Select
                  value={refreshInterval / 1000}
                  onChange={(e) => setRefreshInterval(Number(e.target.value) * 1000)}
                  label="Refresh Interval (seconds)"
                >
                  <MenuItem value={10}>10 seconds</MenuItem>
                  <MenuItem value={15}>15 seconds</MenuItem>
                  <MenuItem value={30}>30 seconds</MenuItem>
                  <MenuItem value={60}>1 minute</MenuItem>
                  <MenuItem value={120}>2 minutes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showFilters}
                    onChange={(e) => setShowFilters(e.target.checked)}
                  />
                }
                label="Show Filters in Dashboard View"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Configuration Management
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportConfig}
                sx={{ mb: 2 }}
              >
                Export Configuration
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<UploadIcon />}
                component="label"
                sx={{ mb: 2 }}
              >
                Import Configuration
                <input
                  type="file"
                  accept=".json"
                  hidden
                  onChange={importConfig}
                />
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RestoreIcon />}
                onClick={resetConfig}
                color="warning"
                sx={{ mb: 2 }}
              >
                Reset to Defaults
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={loadConfig}
                color="info"
                sx={{ mb: 2 }}
              >
                Load Saved Configuration
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={saveConfig} variant="contained" startIcon={<SaveIcon />}>
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default LiveEmployeeStatusPage;
